import { useEffect, useState } from "react";
import { Kv } from "../vault/Common";
import {
  listPlans, getBilling, getInvoices, createCheckout, createPortal,
  type BillingPlan, type BillingState, type BillingInvoice,
} from "../../features/billing/billingApi";

// Compare-plans rows derived from the real plan limits (columns = the live plans),
// not hardcoded copy — keeps the table honest as plans change server-side.
const lim = (n: number) => (n < 0 ? "Unlimited" : n === 0 ? "—" : n.toLocaleString("en-US"));
const CMP_ROWS: [string, (p: BillingPlan) => string][] = [
  ["Models", (p) => lim(p.limits.maxModels)],
  ["Configurators", (p) => lim(p.limits.maxConfigurators)],
  ["Custom domains", (p) => lim(p.limits.maxDomains)],
  ["Embed views / mo", (p) => lim(p.limits.embedViews)],
  ["Team seats", (p) => lim(p.maxMembers)],
  ["Bring-your-own S3", (p) => (p.storageEnabled ? "✓" : "—")],
];

const money = (n: number, c = "USD", frac = 0) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: c, minimumFractionDigits: frac, maximumFractionDigits: frac }).format(n);
const fmtDate = (unixSecs: number) =>
  new Date(unixSecs * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function BillingOverview() {
  const [yr, setYr] = useState(true);
  const [plans, setPlans] = useState<BillingPlan[] | null>(null);
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listPlans(), getBilling().catch(() => null), getInvoices().catch(() => [])])
      .then(([p, b, inv]) => { setPlans(p); setBilling(b); setInvoices(inv); })
      .catch((e) => { setPlans([]); setInvoices([]); setError(e instanceof Error ? e.message : "Failed to load billing"); });
  }, []);

  const goCheckout = async (planId: string) => {
    setBusy(true); setError(null);
    try { const { checkoutUrl } = await createCheckout(planId); window.location.assign(checkoutUrl); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not start checkout"); setBusy(false); }
  };
  const goPortal = async () => {
    setBusy(true); setError(null);
    try { const { portalUrl } = await createPortal(); window.location.assign(portalUrl); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not open billing portal"); setBusy(false); }
  };

  const current = billing?.plan ?? null;
  const renews = billing?.subscription?.currentPeriodEnd ? fmtDate(billing.subscription.currentPeriodEnd) : null;
  const priceOf = (p: BillingPlan) => (yr ? Math.round(p.annualPrice / 12) : p.monthlyPrice);

  return (
    <>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="page-title">Plans &amp; billing</div>
          <div className="page-sub">
            {current
              ? <>You're on <b>{current.name}</b>{renews ? ` · renews ${renews}` : ""}</>
              : "No active plan"}
          </div>
        </div>
        <div className="bill-toggle">
          <button className={yr ? "" : "on"} onClick={() => setYr(false)}>Monthly</button>
          <button className={yr ? "on" : ""} onClick={() => setYr(true)}>Yearly <span style={{ color: "var(--accent)", fontWeight: 700 }}>2 months free</span></button>
        </div>
      </div>
      {error && <div className="page-sub" style={{ color: "var(--danger, #c0392b)" }}>{error}</div>}
      <div className="plans">
        {plans === null ? (
          <div className="page-sub">Loading plans…</div>
        ) : plans.length === 0 ? (
          <div className="page-sub">No plans available yet.</div>
        ) : plans.map((p) => {
          const isCurrent = !!current && p._id === current._id;
          const isDowngrade = !isCurrent && current != null && p.monthlyPrice < current.monthlyPrice;
          return (
            <div key={p._id} className={isCurrent ? "plan feat" : "plan"}>
              {isCurrent && <span className="pop-tag">CURRENT PLAN</span>}
              <div className="p-name">{p.name}</div><div className="p-for">{p.description}</div>
              <div className="p-price">
                {p.monthlyPrice === 0
                  ? <>{money(0)} <small>forever</small></>
                  : <>{money(priceOf(p))} <small>/ month{yr ? ", billed yearly" : ""}</small></>}
              </div>
              <ul>{p.features.map((f) => <li key={f}>✓ {f}</li>)}</ul>
              {isCurrent ? (
                <button className="btn gold" style={{ justifyContent: "center" }} disabled={busy} onClick={goPortal}>Manage plan</button>
              ) : isDowngrade ? (
                <button className="btn ghost" style={{ justifyContent: "center" }} disabled={busy} onClick={goPortal}>Downgrade</button>
              ) : (
                <button className="btn primary" style={{ justifyContent: "center" }} disabled={busy} onClick={() => goCheckout(p._id)}>Upgrade to {p.name}</button>
              )}
            </div>
          );
        })}
      </div>
      {plans && plans.length > 0 && (
        <div className="card" style={{ marginTop: 18 }}>
          <h3>Compare plans</h3>
          <table className="cmp">
            <thead><tr><th></th>{plans.map((p) => <th key={p._id}>{p.name}</th>)}</tr></thead>
            <tbody>
              {CMP_ROWS.map(([label, cell]) => (
                <tr key={label}><td>{label}</td>{plans.map((p) => <td key={p._id}>{cell(p)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>Invoices</h3>
        {invoices === null ? (
          <div className="page-sub">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="page-sub">No invoices yet.</div>
        ) : invoices.map((inv) => (
          <Kv
            key={inv.id}
            k={inv.number ?? inv.id}
            v={<>
              {inv.created ? `${fmtDate(inv.created)} · ` : ""}
              {money(inv.total / 100, inv.currency.toUpperCase(), 2)}
              {inv.pdfUrl && <> · <a href={inv.pdfUrl} target="_blank" rel="noreferrer">PDF</a></>}
            </>}
          />
        ))}
      </div>
    </>
  );
}
